import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Button, Input, message, Modal, Spin, Tag } from 'antd';
import { connect, useDispatch } from 'react-redux';
import RestHelper from 'utils/RestHelper';
import { useTranslation } from 'react-i18next';
import { ze } from 'utils/zhEn';
import scss from './AiDrawer.scss';
import { isNotNullish } from 'utils/nullish';
import useZuStore from 'store/zustand/zustand-store';
import AiChatButtons from 'components/top/AiDrawer/AiChatButtons';
import { ClipboardUtil } from 'utils/clipboard-util';
import { CS_STYLES } from 'utils/constants/style-constants';
import { AiDrawerHooks } from 'components/top/AiDrawer/ai-drawer-hooks';

const AsmCodeLine = ({
  line,
}) => {
  const [code, comment] = useMemo(() => {
    return (line || '').split(';').map(x => x.trim());
  }, [line]);

  return <div>
    <div>{ code }</div>
    <div style={{ color: CS_STYLES.SUB_TEXT_COLOR, marginBottom: '4px', fontSize: '12px' }}>{ comment }</div>
  </div>;
};

const AiAssemblyAnalysisDrawerBody = (props) => {
  const {
    reduxState,
    crashId,
    asmCode,
    trigger,
  } = props;

  const aiDrawerSlice = useZuStore().aiDrawer;

  const { t } = useTranslation();
  const [asmCodeExplainText, setAsmCodeExplainText] = useState('');
  const [asmCodeExplainLoading, setAsmCodeExplainLoading] = useState(false);
  const [abortController, setAbortController] = useState(null);
  // 使用 ref 保存 abortController 的引用，确保在组件卸载时能拿到最新的值
  const abortControllerRef = useRef(null);

  const asmCodeExplainLines = useMemo(() => {
    return (asmCodeExplainText || '').split('\n').map(x => x.trim());
  }, [asmCodeExplainText]);

  const currentApp = reduxState.app.get('current').toJS();
  const { appId, pid } = currentApp;

  const [aiResultId, setAiResultId] = useState('');
  const { isLike, isDislike, onClickLikeOrDislike } = AiDrawerHooks.useLikeDislike(appId, aiResultId);

  // 同步 abortController 到 ref
  useEffect(() => {
    abortControllerRef.current = abortController;
  }, [abortController]);

  // 组件卸载时中断请求（切换到不同汇编代码时）
  useEffect(() => {
    return () => {
      // 使用 ref 获取最新的 abortController 并中断
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  useEffect(() => {
    if (!trigger) {
      return;
    }
    startExplainAsmCodeByAi();
  }, [trigger]);

  function startExplainAsmCodeByAi(clearCache = false) {
    if (isNotNullish(abortController)) {
      abortController.abort();
      setAbortController(null);
    }
    setAsmCodeExplainText('');
    setAsmCodeExplainLoading(true);
    setAiResultId('');

    const url = '/api/aiPilot/explainAssemblyCode';
    const params = {
      appId,
      crashId,
      code: asmCode,
      clearCache,
    };
    const ac = new AbortController();
    setAbortController(ac);
    RestHelper.postEventStream(url, params, {
      signal: ac.signal,
      statusCodeToCallback: {
        500() {
          message.error(ze('后台正忙，请稍后重试','The server is busy, please try again later'));
        },
      },
      onmessage(event) {
        const rawData = event.data;
        if (!rawData) {
          return;
        }
        let data = rawData;
        try {
          data = JSON.parse(rawData);
        } catch (e) {
          console.error(`explainAsmCodeByAi error when parsing: `, rawData);
        }

        if (data.includes('AiAnalyseResult')){
          const pattern = /\d+/;
          const match = pattern.exec(data);
          const id = match[0];
          setAiResultId(id);
        } else if (data) {
          setAsmCodeExplainLoading(false);
          setAsmCodeExplainText(oldValue => {
            if (!!data.trim() || !!oldValue) {
              return oldValue + data;
            } else {
              return oldValue;
            }
          });
        }
      },
    });
  }

  async function onClickCopy() {
    await ClipboardUtil.copyToClipboard(asmCodeExplainText);
    message.success(ze('已复制至剪贴板', 'Copied to clipboard.'));
  }

  return <div
    className={scss.issueAiAnalysisDrawerBody}
  >
    <div>
      <div
        style={{ whiteSpace: 'pre-wrap', marginBottom: '24px' }}
      >{
        asmCodeExplainLines.map((line, i) => <AsmCodeLine key={i} line={line}/>)
      }</div>
    </div>
    { !!aiResultId && <AiChatButtons
      showLikeButtons={!!aiResultId}
      isLikeSelected={isLike}
      isDislikeSelected={isDislike}
      onLike={() => onClickLikeOrDislike(true)}
      onDislike={() => onClickLikeOrDislike(false)}
      onCopy={() => onClickCopy()}
      onRefresh={() => startExplainAsmCodeByAi(true)}
    /> }
  </div>;
};


const mapStateToProps = (state, router) => ({
  reduxState: state,
});

export default connect(mapStateToProps)(AiAssemblyAnalysisDrawerBody);
